import React, { useState } from 'react';
import { BellIcon, EyeOffIcon, MailIcon, PlusIcon, SendIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Panel } from '../ui/Panel';
import { Modal } from '../ui/Modal';
import { Field, Input, Select, Textarea } from '../ui/Field';
import { referenceRequests as seed } from '../../data/services';
import type { ReferenceKind, ReferenceRequest, ReferenceStatus } from '../../types/services';
const KINDS: ReferenceKind[] = ['Academic recommendation', 'Academic reference', 'Professional reference', 'Character reference', 'Scholarship recommendation', 'Graduate school recommendation', 'Internship recommendation', 'Employment reference'];
const STATUS_TONE: Record<ReferenceStatus, 'neutral' | 'warn' | 'brand' | 'ok' | 'danger'> = {
  Draft: 'neutral',
  'Awaiting referee': 'warn',
  Declined: 'danger',
  Accepted: 'brand',
  'In preparation': 'brand',
  Submitted: 'ok',
  Verified: 'ok'
};

/** The student half of the referee workflow: request, chase, and never read a confidential letter. */
export function ReferencePanel() {
  const [requests, setRequests] = useState<ReferenceRequest[]>(seed);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    kind: KINDS[5] as ReferenceKind,
    refereeName: '',
    refereeRole: '',
    refereeEmail: '',
    recipient: '',
    deadline: '',
    confidential: true,
    method: 'Direct to recipient' as ReferenceRequest['submissionMethod'],
    notes: ''
  });
  function submit() {
    const record: ReferenceRequest = {
      id: `REF-2026-${String(1000 + requests.length).slice(1)}`,
      kind: form.kind,
      refereeName: form.refereeName,
      refereeRole: form.refereeRole,
      refereeEmail: form.refereeEmail,
      recipient: form.recipient,
      confidential: form.confidential,
      submissionMethod: form.method,
      deadline: form.deadline || 'No deadline set',
      status: 'Awaiting referee',
      requestedOn: '14 Aug 2026',
      respondedOn: null,
      submittedOn: null,
      attachments: ['CV — Amarachi Obi.pdf'],
      remindersSent: 0,
      notesForReferee: form.notes
    };
    setRequests(current => [record, ...current]);
    setOpen(false);
    setForm({
      ...form,
      refereeName: '',
      refereeRole: '',
      refereeEmail: '',
      recipient: '',
      notes: ''
    });
  }
  return <>
      <Panel title="References and recommendations" description="A category of Academic Services. Your referee writes and submits — Campuzon only carries the request." action={<Button size="sm" variant="primary" icon={<PlusIcon className="h-3.5 w-3.5" data-id="element-4223" data-mp="2wmz4u8ih6x1g" />} onClick={() => setOpen(true)} data-id="element-4222" data-mp="2wlxqnv65xufi">
            Request a reference
          </Button>} data-id="element-4221" data-mp="35ehlekjc3ev2">
        <ul className="space-y-3" data-id="element-4224" data-mp="2mo03zc3yc5p0">
          {requests.map(request => <li key={request.id} className="rounded-card border border-line-strong bg-surface px-4 py-3.5" data-id="element-4225" data-mp="2mq46bpm7cwn6">
              <div className="flex flex-wrap items-center gap-2" data-id="element-4226" data-mp="2hr9dduuitrpc">
                <p className="text-[14px] font-semibold text-ink" data-id="element-4227" data-mp="27k6v1g90cla6">{request.refereeName}</p>
                <Badge tone={STATUS_TONE[request.status]} data-id="element-4228" data-mp="2ciru3cciwyx1">{request.status}</Badge>
                {request.confidential && <Badge tone="neutral" icon={<EyeOffIcon className="h-3.5 w-3.5" data-id="element-4230" data-mp="2mi8v3ggtyomo" />} data-id="element-4229" data-mp="2hjxqpl9b16bu">
                    Confidential
                  </Badge>}
                <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-4231" data-mp="1ovjpyxqtzvwl">{request.id}</span>
              </div>
              <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-4232" data-mp="1tud14jinbatl">{request.refereeRole}</p>

              <dl className="mt-2.5 grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-4" data-id="element-4233" data-mp="1yrmz4nk3out1">
                {[{
              term: 'Kind',
              value: request.kind
            }, {
              term: 'For',
              value: request.recipient
            }, {
              term: 'Due',
              value: request.deadline
            }, {
              term: 'Submission',
              value: request.submissionMethod
            }].map(row => <div key={row.term} data-id="element-4234" data-mp="3ctaztarvokmm">
                    <dt className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4235" data-mp="3cq6lvlom2air">
                      {row.term}
                    </dt>
                    <dd className="mt-0.5 text-[13px] leading-6 text-ink" data-id="element-4236" data-mp="3miukmlz5iok4">{row.value}</dd>
                  </div>)}
              </dl>

              <ol className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]" data-id="element-4237" data-mp="3vir2a3n4in5o">
                {([['Requested', request.requestedOn], [request.status === 'Declined' ? 'Declined' : 'Accepted', request.respondedOn ?? null], ['Submitted', request.submittedOn ?? null]] as [string, string | null][]).map(([label, at], index) => <li key={label} className="flex items-center gap-2" data-id="element-4238" data-mp="264clc5ick4kq" data-mpi={index}>
                    {index > 0 && <span className="text-ink-subtle" data-id="element-4239" data-mp="lm2109l488hg">→</span>}
                    <span className={at ? 'font-medium text-ink' : 'text-ink-subtle'} data-id="element-4240" data-mp="lmk1jiet98v6">
                      {label}
                      {at ? ` · ${at}` : ' · pending'}
                    </span>
                  </li>)}
              </ol>

              {request.confidential ? <p className="mt-2.5 rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-4241" data-mp="2tzeihlslxza">
                  This reference is confidential. You can see whether it was accepted and submitted, but
                  never its contents — not before submission, not after.
                </p> : <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-4242" data-mp="1nam8m04s7kmw">
                  Not confidential: {request.refereeName} chose to let you read it once submitted.
                </p>}

              {request.status === 'Awaiting referee' && <div className="mt-2.5 flex flex-wrap items-center gap-2" data-id="element-4243" data-mp="1qqp3jyg0ulue">
                  <Button size="sm" variant="secondary" icon={<BellIcon className="h-3.5 w-3.5" data-id="element-4245" data-mp="1gv5mgi649rpp" />} onClick={() => setRequests(current => current.map(item => item.id === request.id ? {
              ...item,
              remindersSent: item.remindersSent + 1
            } : item))} data-id="element-4244" data-mp="1lsdwfvh42pfv">
                    Send a reminder
                  </Button>
                  <span className="text-[12px] text-ink-muted" data-id="element-4246" data-mp="1vq20xd4irbb8">
                    {request.remindersSent === 0 ? 'No reminder sent yet.' : `${request.remindersSent} reminder${request.remindersSent === 1 ? '' : 's'} sent.`}
                  </span>
                </div>}
            </li>)}
        </ul>
      </Panel>

      <Modal open={open} onClose={() => setOpen(false)} title="Request a reference" description="Your referee receives an invitation and decides whether to act. They write it themselves." width="lg" footer={<>
            <Button variant="ghost" onClick={() => setOpen(false)} data-id="element-4248" data-mp="syhck4sqmxqw">
              Cancel
            </Button>
            <Button variant="primary" icon={<SendIcon className="h-4 w-4" data-id="element-4250" data-mp="12plqr2alaeko" />} disabled={!form.refereeName || !form.refereeEmail || !form.recipient} onClick={submit} data-id="element-4249" data-mp="xxc5w0oc04p9">
              Send request
            </Button>
          </>} data-id="element-4247" data-mp="f0awm3nqvifi">
        <div className="space-y-3.5" data-id="element-4251" data-mp="1brl8xxxcbop7">
          <Field label="Kind of reference" data-id="element-4252" data-mp="1gp4mswq4lzgm">
            <Select value={form.kind} onChange={event => setForm({
            ...form,
            kind: event.target.value as ReferenceKind
          })} data-id="element-4253" data-mp="1gq677rt17yb0">
              {KINDS.map(kind => <option key={kind} data-id="element-4254" data-mp="mm36s2fasab3">{kind}</option>)}
            </Select>
          </Field>
          <div className="grid gap-3.5 sm:grid-cols-2" data-id="element-4255" data-mp="10m46hejttboz">
            <Field label="Referee name" data-id="element-4256" data-mp="wlbzkryffivv">
              <Input value={form.refereeName} onChange={event => setForm({
              ...form,
              refereeName: event.target.value
            })} placeholder="Prof. N. Okonkwo" data-id="element-4257" data-mp="wlkaieazoe4f" />
            </Field>
            <Field label="Their role" data-id="element-4258" data-mp="3uk7a5khfedt">
              <Input value={form.refereeRole} onChange={event => setForm({
              ...form,
              refereeRole: event.target.value
            })} placeholder="Head of Department" data-id="element-4259" data-mp="ho1r7rh2yt4d" />
            </Field>
            <Field label="Their email" data-id="element-4260" data-mp="3lb1wr0pco00w">
              <Input type="email" value={form.refereeEmail} onChange={event => setForm({
              ...form,
              refereeEmail: event.target.value
            })} placeholder="name@university.edu.ng" data-id="element-4261" data-mp="3hbt6w6xfkmam" />
            </Field>
            <Field label="Deadline" data-id="element-4262" data-mp="3bzw6yh642ces">
              <Input value={form.deadline} onChange={event => setForm({
              ...form,
              deadline: event.target.value
            })} placeholder="20 Sep 2026" data-id="element-4263" data-mp="2x9yc5idoeogm" />
            </Field>
          </div>
          <Field label="Who it is for" data-id="element-4264" data-mp="3pym0xnxmvvyx">
            <Input value={form.recipient} onChange={event => setForm({
            ...form,
            recipient: event.target.value
          })} placeholder="University of Leeds — MSc Energy Systems" data-id="element-4265" data-mp="3uwnk08v27vae" />
          </Field>
          <Field label="How it should be submitted" data-id="element-4266" data-mp="3kx6g9wzdbrh2">
            <Select value={form.method} onChange={event => setForm({
            ...form,
            method: event.target.value as ReferenceRequest['submissionMethod']
          })} data-id="element-4267" data-mp="24krdmje81yvd">
              <option data-id="element-4268" data-mp="wg4dprtze65s">Direct to recipient</option>
              <option data-id="element-4269" data-mp="whfi9tv93hxy">Returned to student</option>
              <option data-id="element-4270" data-mp="11d4miai7tn0l">Campuzon verified link</option>
            </Select>
          </Field>
          <Field label="Anything useful for your referee" hint="They see this; it is not part of the letter." data-id="element-4271" data-mp="mq1m9w2id09k">
            <Textarea rows={2} value={form.notes} onChange={event => setForm({
            ...form,
            notes: event.target.value
          })} placeholder="The project or coursework most relevant to this application…" data-id="element-4272" data-mp="rnbcvf1mvhyz" />
          </Field>
          <label className="flex items-start gap-2.5" data-id="element-4273" data-mp="3r78apavc3ug">
            <input type="checkbox" checked={form.confidential} onChange={event => setForm({
            ...form,
            confidential: event.target.checked
          })} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" data-id="element-4274" data-mp="3si8yg90ovhc" />
            <span data-id="element-4275" data-mp="2328edof07zn8">
              <span className="block text-[13px] text-ink" data-id="element-4276" data-mp="232qgv2s9fa3l">Keep this reference confidential</span>
              <span className="mt-0.5 block text-[12px] leading-5 text-ink-muted" data-id="element-4277" data-mp="3nltx14mwwba8">
                Many universities require it. You will never be able to read the letter, and Campuzon
                will not show it to you at any stage.
              </span>
            </span>
          </label>
          <p className="flex items-start gap-2 rounded-lg border border-line bg-canvas px-3.5 py-3 text-[12px] leading-5 text-ink-muted" data-id="element-4278" data-mp="3sjca977ypco1">
            <MailIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-4279" data-mp="65cfcvoactxp" />
            Campuzon never drafts, edits or signs a reference, and never submits one on a referee’s
            behalf.
          </p>
        </div>
      </Modal>
    </>;
}